import { IcoAdd, IcoFile, IcoRadio } from "assets/icons";
import IcoOficialVehicle from "assets/icons/ico-oficial-vehicle";
import IcoPoliceVehicle from "assets/icons/ico-police-vehicle";
import IcoRentedVehicle from "assets/icons/ico-rented-vehicle";
import IcoResident from "assets/icons/ico-resident";
import { ErrorHelper } from "helpers";
import { OptionLike } from "helpers/OptionHelper";
import { useDataModel } from "hooks/useDataModel";
import { Concessionaire } from "models/Concessionaire";
import { ExemptionType } from "models/ExemptionType";
import { Fragment, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { ExemptionTypeService } from "services/exemptionType";
import { StepLoading } from "../layout";
import { ApplicationFields, ApplicationFormik } from "../types";

export default function Exemption({ formik }: ApplicationFormik) {
	const { concessionaire } = formik.values;
	const selectedConcessionaire = useDataModel(concessionaire, Concessionaire);
	const name = ApplicationFields.exemptionType;
	const value = formik.values.exemptionType?.id;
	const error = formik.errors.exemptionType;
	const touched = formik.touched.exemptionType;
	const exemptionTypeService = useMemo(ExemptionTypeService.getInstance, []);
	const [exemptionTypes, setExemptionTypes] = useState<(OptionLike & ExemptionType)[]>([]);
	const [isLoadingTypes, setIsLoadingTypes] = useState(false);

	const getIconByLabel = (label: string) => {
		const params = { size: "24", color: "#1D1D1D" };

		switch (label.toLowerCase()) {
			case "morador":
				return <IcoResident {...params} size="18" />;
			case "determinação judicial":
				return <IcoFile {...params} />;
			case "veículo oficial":
				return <IcoOficialVehicle {...params} />;
			case "veículo locado":
				return <IcoRentedVehicle {...params} />;
			case "prf particular":
				return <IcoPoliceVehicle {...params} />;
			default:
				return <IcoAdd {...params} size="20" />;
		}
	};

	useEffect(() => {
		if (!selectedConcessionaire) {
			setExemptionTypes([]);
			return;
		}

		setIsLoadingTypes(true);
		exemptionTypeService
			.listByConcessionaire(selectedConcessionaire.id)
			.then((response) => {
				if (response.length === 0) {
					toast.info("Não há isenções disponíveis para essa concessionária.");
				}
				setExemptionTypes(response.map((exemptionType) => exemptionType.toOption()));
			})
			.catch((e) => toast.error(ErrorHelper.getResponseErrorMessage(e)))
			.finally(() => setIsLoadingTypes(false));
	}, [selectedConcessionaire?.id]);

	const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const selectedExemption = exemptionTypes.find((exemptionType) => exemptionType.value === e.target.value);
		formik.setFieldValue(name, selectedExemption);
	};

	return (
		<div className="h-full overflow-y-auto flex flex-col items-center max-w-2xl">
			{isLoadingTypes ? (
				<StepLoading />
			) : (
				<div className="w-full md:mb-10 px-8 py-6 flex flex-col gap-4">
					<h1 className="mb-6 md:my-2 md:p text-2xl font-semibold md:text-lg md:font-normal ">
						Selecione uma isenção:
						{error && touched && (
							<div className="text-xs text-feedback-negative-pure-500 font-normal">
								{Array.isArray(error) ? error.join("\n") : error}
							</div>
						)}
					</h1>
					<fieldset>
						{exemptionTypes.map((exemptionType, idx) => (
							<Fragment key={exemptionType.value}>
								<div className="cursor-pointer">
									<input
										id={`${name}-${exemptionType.value}`}
										name={name}
										value={exemptionType.value}
										onChange={onChange}
										type="radio"
										checked={exemptionType.value === value}
										hidden
									/>
									<label htmlFor={`${name}-${exemptionType.value}`} className="flex flex-col gap-1 cursor-pointer">
										<div className="flex justify-between items-center">
											<div className="flex gap-2 items-center">
												{getIconByLabel(exemptionType.label! as string)}
												<span className="font-semibold">{exemptionType.label}</span>
											</div>
											<IcoRadio filled={exemptionType.value === value} />
										</div>
										<div>{exemptionType.description}</div>
									</label>
								</div>
								{idx < exemptionTypes.length - 1 && <div className="border-b border-neutral-high-200 my-4" />}
							</Fragment>
						))}
					</fieldset>
				</div>
			)}
		</div>
	);
}
